import { dangerouslyUseRawSQL, joinSQLFragments, SQL, type SQLFragment } from '@/client/query'

interface ComposerContext {
  alias: (alias: string) => SQLFragment<string>
  ref: (alias: string) => SQLFragment<string>
}

export default function composer(
  callback: (context: ComposerContext) => {
    from: {
      alias: SQLFragment<string>
      id: SQLFragment<unknown>
      where: SQLFragment<unknown>
      materialized?: boolean
    }[]
    gather: {
      id: SQLFragment<unknown>
      where: SQLFragment<unknown>
    }
  }
) {
  const { from, gather } = callback({
    alias: name => dangerouslyUseRawSQL({ __sql: JSON.stringify(`${name}_composed_ref`) }),
    ref: name =>
      SQL`ANY(SELECT * FROM ${dangerouslyUseRawSQL({ __sql: JSON.stringify(`${name}_composed_ref`) })})`,
  })

  if (from.length === 0) {
    throw new Error('Cannot compose an empty query')
  }

  return SQL`(
    WITH ${joinSQLFragments(
      from.map(
        item =>
          SQL`${item.alias} AS ${
            item.materialized == null
              ? SQL``
              : item.materialized
                ? SQL`MATERIALIZED`
                : SQL`NOT MATERIALIZED`
          } (SELECT ${item.id} ${item.where})`
      ),
      ', '
    )}
    SELECT ${gather.id} ${gather.where}
  )`
}

/**
 * Example Usage: (You can also use this on the frontend, example just embeds it in runQuery)
 *
 * const relevantSessions = composer(({ alias, ref }) => ({
 *   from: [
 *     {
 *       alias: alias('topic'),
 *       id: TopicQ.id,
 *       where: TopicQ.where({}, SQL`${TopicQ.name} != ${'Live Help'}`),
 *     },
 *   ],
 *   gather: {
 *     id: SessionQ.id,
 *     where: SessionQ.where({ topicId: ref('topic') }),
 *   },
 * }))
 *
 * const sessions = await runQuery(SQL`
 *   SELECT * FROM ${SessionQ} WHERE ${SessionQ.id} IN (${relevantSessions})
 * `)
 *
 * For the above query, you get the following SQL out:
 *
 * SELECT * FROM "session" WHERE "session"."id" IN ((
 *   WITH "topic_composed_ref" AS (SELECT "topic"."id"
 *       FROM "topic"
 *       WHERE
 *           active AND
 *           "topic"."deleted_at" IS NULL AND
 *           "topic"."name" != 'Live Help'
 *   )
 *   SELECT "session"."id"
 *     FROM "session"
 *     WHERE
 *         "session"."topic_id" = ANY(SELECT * FROM "topic_composed_ref") AND
 *         "session"."deleted_at" IS NULL AND
 *         TRUE
 * ))
 *
 */
